import React, { useEffect, useMemo } from "react";
import { inject, observer } from "mobx-react";

import { useFormik } from "formik";

import { CircularProgress, makeStyles } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";

import { REGEX } from "../../lib/constants";
import gpt3Encoder from "../../modules/gpt-3-encoder";

import useChatModel from "../../hooks/useChatModel";
import { camelToNormal, makeKey } from "../../lib/utils";
import Progress from "app/Chat/Progress";

const MAX_NAME_LENGTH = 31;
const FIELD_NAME_PREFIX = "sheet_name_for_";
const MAX_NAME_SUGGESTION_ITERATION = 5;

const useStyle = makeStyles((theme) => ({
  title: {
    fontSize: 14,
    fontWeight: "normal",
  },
  textMuted: {
    color: "#818181",
  },
  textError: {
    color: "red",
  },
  container: {
    padding: "1rem",
  },
  list__container: {
    listStyleType: "none",
    padding: 0,
  },
  list__item: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: ".5rem",
    justifyContent: "space-between",
    padding: "0.5rem 0",

    "&.deleted *": {
      textDecoration: "line-through !important",
    },
  },
  formContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: ".5rem",
    margin: "0.5rem 0",
  },
  action__container: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  actions: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: ".5rem",
  },
}));

/**
 * 1. list of sheets which exist -> duplicateSheets (another array)
 * 2. separate list for existing sheets, which is needed to be replaced with another name
 * 2. fetch suggestion from AI
 * 3. If suggested sheet exists, add it to duplicateSheets
 * 4. Else add to newSheetsNames
 * 5. till iteration 5, keep fetching the suggestion
 * 6.
 *
 */

const SheetsUpdater = (props) => {
  const {
    params,
    baseChatModel,
    onCancel,
    onSubmit,
    onFetchChatsAccessToken,
    //
    //
    sheetIdsByName,

    url,
    body,
    headers,
    models,
    modelVariants,
    useMatchingTokenModel,

    chatHelpersStore,
    assistantStore,
    updaterStore,
    parentComponent
  } = props;

  const classes = useStyle();

  const handleChatFinish = async (data) => {
    try {
      const { choices = [] } = data;
      if (!choices.length) {
        throw new Error();
      }

      const [
        {
          message: {
            function_call: { arguments: args },
          },
        },
      ] = choices;
      if (!args) {
        throw new Error();
      }
      updaterStore.setFunctionResponse(args);
      updaterStore.setFnTriggerCount(updaterStore.fnTriggerCount + 1);
    } catch (e) {
      console.log(`e->`, e);
      const existingSheets = updaterStore.existingSheets;
      const sheetsNeedNameUpdate = updaterStore.sheetsNeedNameUpdate;
      await suggestSheetNames({ existingSheets, sheetsNeedNameUpdate });
      return;
    }
  };

  const chatModelData = useChatModel({
    models,
    modelVariants,
    baseChatModel,
    useMatchingTokenModel,
  });

  const suggestSheetNames = async ({
    existingSheets,
    sheetsNeedNameUpdate,
  }) => {
    if (updaterStore.iterations >= MAX_NAME_SUGGESTION_ITERATION) {
      updaterStore.setCannotSuggestNames(true);
      updaterStore.setLoading(false);
      return;
    }

    try {
      updaterStore.setIterations(updaterStore.iterations + 1);
      updaterStore.setLoading(true);

      const _existingSheets = Object.values(existingSheets)
      const _sheetsNeedNameUpdate =
        Object.values(sheetsNeedNameUpdate)

      let str = "";

      if (_existingSheets.length) {
        str +=
          "These are the existing sheets:\n" +
          _existingSheets +
          "\n\nSuggest me another name for following sheets:\n" +
          _sheetsNeedNameUpdate;
      } else {
        str +=
          "These are the existing sheets:\n" +
          _sheetsNeedNameUpdate +
          "\n\nSuggest me another name for every existing sheet";
      }

      const messages = [
        {
          role: "user",
          content: str,
          type: "message",
        },
      ];

      const properties = {};
      const required = [];

      for (let item of _sheetsNeedNameUpdate) {
        properties[item] = {
          type: "string",
        };
        required.push(item);
      }

      const functions = [
        {
          name: "getNewSheetNames",
          description: "New names for sheets which are already existing",
          parameters: {
            type: "object",
            properties: {
              sheets: {
                type: "object",
                description:
                  "Key-value pair of previous name and new name. Key is old name and value is new name of the sheet.",
                properties,
                required,
              },
            },
            required: ["sheets"],
          },
        },
      ];

      const encodedMessages = gpt3Encoder.encode(
        JSON.stringify({ messages, functions })
      );

      const tokensSize = encodedMessages.length;

      const model = chatModelData.getModel({
        tokensSize,
      });

      const { token } = await onFetchChatsAccessToken();

      const res = await chatHelpersStore.getChatCompletion(
        url,
        {
          ...body,
          stream: false,
          include: undefined,
          function_call: { name: "getNewSheetNames" },
          model: model.name,
          messages,
          functions,
        },
        {
          headers: {
            ...headers,
            Authorization: `Bearer ${token}`,
          },
        }
      );

      await handleChatFinish(res.data);
    } catch (e) {
      console.log(`e->`, e);
      await suggestSheetNames({ existingSheets, sheetsNeedNameUpdate });
    }
  };

  const loadInitialData = async () => {
    try {
      updaterStore.setLoading(true);
      await assistantStore.fetchAndLoadSheets();

      const existingSheets = {};

      for (const sheet of params) {
        existingSheets[makeKey(sheet.sheetName)] = sheet.sheetName
      }

      updaterStore.setExistingSheets(existingSheets);
      updaterStore.setSheetsNeedNameUpdate(existingSheets);

      await suggestSheetNames({
        existingSheets,
        sheetsNeedNameUpdate: existingSheets,
      });
    } catch (e) {
      console.log(`e->`, e);
      updaterStore.setErrorMessage(e.message);
      updaterStore.setLoading(false);
    }
  };

  const saveUpdatesSheets = async (sheets) => {
    try {
      updaterStore.setSubmitting(true);
      updaterStore.setErrorMessage("");
      await onSubmit(sheets);
      updaterStore.resetState();
    } catch (e) {
      console.log(`e->`, e);
      updaterStore.setErrorMessage(e.message);
      updaterStore.setSubmitting(false);
    }
  };

  const handleFnTrigger = async () => {
    const existingSheets = updaterStore.existingSheets;
    const sheetsNeedNameUpdate = updaterStore.sheetsNeedNameUpdate;

    try {
      if (!updaterStore.fnTriggerCount) return;
      const args = JSON.parse(updaterStore.functionResponse);

      if (!args.sheets) {
        await suggestSheetNames({ existingSheets, sheetsNeedNameUpdate });
        updaterStore.setLoading(false);
        return;
      }

      const _existingSheets = { ...existingSheets };
      const _sheetsNeedNameUpdate = { ...sheetsNeedNameUpdate };
      const _sheetsGotNewName = { ...updaterStore.sheetsGotNewName };

      let responseGotDuplicateSheet = false;
      for (let oldSheet in args.sheets) {
        const oldSheetNameKey = makeKey(oldSheet);
        const name = args.sheets[oldSheet]?.trim();
        const sheetNameKey = makeKey(name);
        if (!name || sheetIdsByName[sheetNameKey]) {
          _existingSheets[sheetNameKey] = name
          responseGotDuplicateSheet = true;
        } else {
          _sheetsGotNewName[oldSheetNameKey] = name;
          delete _sheetsNeedNameUpdate[oldSheetNameKey];
        }
      }

      updaterStore.setExistingSheets(_existingSheets);
      updaterStore.setSheetsNeedNameUpdate(_sheetsNeedNameUpdate);
      updaterStore.setSheetsGotNewName(_sheetsGotNewName);

      if (responseGotDuplicateSheet) {
        suggestSheetNames({
          existingSheets: _existingSheets,
          sheetsNeedNameUpdate: _sheetsNeedNameUpdate,
        });
        return;
      }

      const newSheets = [];
      for (const sheet of params) {
        newSheets.push({
          ...sheet,
          newSheetName: _sheetsGotNewName[makeKey(sheet.sheetName)],
        });
      }

      updaterStore.setLoading(false);
      await saveUpdatesSheets(newSheets);
    } catch (e) {
      console.log(`e->`, e);
      await suggestSheetNames({ existingSheets, sheetsNeedNameUpdate });
      return;
    }
  };

  const handleSubmit = async (formValues) => {
    const sheetsWithUpdatedNames = params.map((sheet) => {
      const name = makeKey(sheet.sheetName);
      if (updaterStore.sheetsGotNewName[name]) {
        sheet.newSheetName = camelToNormal(updaterStore.sheetsGotNewName[name]);
      } else {
        const fieldName = `${FIELD_NAME_PREFIX}${name}`;
        sheet.newSheetName = (formValues[fieldName] || "").trim();
      }
      return sheet;
    });

    await saveUpdatesSheets(sheetsWithUpdatedNames);
  };

  const handleValidateForm = (values) => {
    const errors = {};

    for (let name in updaterStore.sheetsNeedNameUpdate) {
      const fieldName = `${FIELD_NAME_PREFIX}${name}`;
      const value = values[fieldName].trim();
      if (!value) {
        errors[fieldName] = `Please enter the sheet name.`;
        continue;
      }

      if (sheetIdsByName[value]) {
        errors[
          fieldName
        ] = `Sheet name "${value}" is already available. Please try some another name.`;
        continue;
      }

      if (value.length > MAX_NAME_LENGTH) {
        errors[
          fieldName
        ] = `The sheet name should not exceed ${MAX_NAME_LENGTH} characters.`;
        continue;
      }

      if (!REGEX.name.test(value)) {
        errors[fieldName] =
          "The sheet name shouldn't contain any special character. It should have at least one alphabet character. Use underscore and numbers (if needed).";
      }
    }

    return errors;
  };

  const initialValues = useMemo(() => {
    const values = {};
    for (let item of params) {
      const name = makeKey(item.sheetName)
      const fieldName = `${FIELD_NAME_PREFIX}${name}`;
      let sheetName = name;
      let unParsedSheetName = name;
      if (sheetIdsByName[name]) {
        let i = 1;
        while (true) {
          unParsedSheetName = `${name}_${i}`
          sheetName = `${name}${i}`;
          if (!sheetIdsByName[sheetName]) {
            break;
          }
          i++;
        }
      }
      values[fieldName] = unParsedSheetName;
    }
  //  values_sample = {
  //     "sheet_name_for_users": "users_1",
  //     "sheet_name_for_categories": "categories_1",
  //     "sheet_name_for_tags": "tags_1",
  //     "sheet_name_for_notes": "notes_1"
  // }
    if (parentComponent == "ChatDialog") handleSubmit(values);
    else return values;
  }, [params]);

  const formik = useFormik({
    initialValues,
    validate: handleValidateForm,
    onSubmit: handleSubmit,
  });

  const description = useMemo(() => {
    if (!params.length) return "";

    if (params.length === 1)
      return `Seems like "${params[0].sheetName}" sheet already exists.`;

    const [first, ...restSheets] = params;
    return `Seems like ${restSheets
      .map((param) => `"${param.sheetName}"`)
      .join(", ")} and "${first.sheetName}" sheets already exist.`;
  }, [params]);

  const handleCancel = () => {
    onCancel();
  };

  const renderSheetUpdaterContent = () => {
      const sheets = Object.keys(updaterStore.sheetsNeedNameUpdate);
      const updatedSheets = Object.keys(updaterStore.sheetsGotNewName);
      return (
        <>
          <h4
            className={classes.title}
            style={{ fontSize: 14, fontWeight: "normal" }}
          >
            {description}
          </h4>
          <div>
            {updatedSheets.length > 0
              ? `We have updated names for ${updatedSheets.map(
                  (name) => `${name} as ${updaterStore.sheetsGotNewName[name]}`
                )}`
              : ""}
            <p>Please update the following:</p>
          </div>
          <ul className={classes.list__container}>
            {sheets.map((sheet, i) => {
              const fieldName = `${FIELD_NAME_PREFIX}${sheet}`;
              return (
                <li className={classes.list__item} key={i}>
                  <div>
                    <div>
                      {sheet} <span className={classes.textMuted}>sheet</span>{" "}
                    </div>
                    <div className={classes.formContainer}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        name={fieldName}
                        required
                        value={formik.values[fieldName]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched[fieldName]?.newSheetName &&
                          Boolean(formik.errors[fieldName]?.newSheetName)
                        }
                        helperText={
                          formik.touched[fieldName]?.newSheetName &&
                          formik.errors[fieldName]?.newSheetName
                        }
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className={classes.action__container}>
            <div>
              {updaterStore.errorMessage && (
                <p className={classes.textError}>{updaterStore.errorMessage}</p>
              )}
            </div>
            <div className={classes.actions}>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disableElevation
                disabled={formik.touched && !formik.isValid}
              >
                Continue
              </Button>
            </div>
          </div>
        </>
      );
  };

  const renderSheetsUpdater = () => {
    if (parentComponent == "ChatDialog") return null;
    if (updaterStore.submitting) {
      return (
        <Progress progress={{label:"Changing sheets names"}} parentComponent={parentComponent} />
      );
    }
    if (!updaterStore.loading || updaterStore.cannotSuggestNames) {
      return (
        <form
          noValidate
          className={classes.container}
          onSubmit={formik.handleSubmit}
        >
          {renderSheetUpdaterContent()}
        </form>
      );
    }

    return (
      <div>
        <Progress
          parentComponent={parentComponent}
          progress={{
            label: `${description}. Getting another name${params.length > 1 ? "s" : ""}`,
          }}
        />
      </div>
    );
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    handleFnTrigger();
  }, [updaterStore.fnTriggerCount]);

  useEffect(() => {
    return () => {
      updaterStore.resetState();
    };
  }, []);

  return renderSheetsUpdater();
};

export default inject(({ store }) => {
  return {
    sheetIdsByName: store.assistantStore.sheetIdsByName,
    assistantStore: store.assistantStore,
    chatHelpersStore: store.chatHelpersStore,
    updaterStore: store.sheetsNamesUpdaterStore,
  };
})(observer(SheetsUpdater));
